import bolide_assistant_composer from "./BolideAssistantComposer/BolideAssistantComposer.vue";
import bolide_assistant_header from "./BolideAssistantHeader/BolideAssistantHeader.vue";
import bolide_assistant_history from "./BolideAssistantHistory/BolideAssistantHistory.vue";
import bolide_assistant_messages from "./BolideAssistantMessages/BolideAssistantMessages.vue";
import chat_methods from "./bolide_assistant_chat_methods";
import conversation_methods from "./bolide_assistant_conversation_methods";
import query_methods from "./bolide_assistant_query_methods";

export default {
  name: "BolideAssistant",

  components: {
    BolideAssistantComposer: bolide_assistant_composer,
    BolideAssistantHeader: bolide_assistant_header,
    BolideAssistantHistory: bolide_assistant_history,
    BolideAssistantMessages: bolide_assistant_messages,
  },

  props: {
    is_open: Boolean,
    scope: {
      type: Object,
      default: null,
    },
    compare_property_options: {
      type: Array,
      default: () => [],
    },
    compare_property_ids: {
      type: Array,
      default: () => [],
    },
  },

  emits: ["close-assistant", "update:compare_property_ids"],

  data() {
    return ({
      active_storage_key: "bolide_assist_active_conversation_v1",
      is_fullscreen: false,
      is_history_open: false,
      is_docked: window.innerWidth > 768,
      user_message: "",
      is_sending: false,
      typewriter_timer: null,
      conversations: [],
      active_conversation_id: null,
      chat_history_next_cursor: null,
      is_loading_chat_history: false,
      has_loaded_backend_history: false,
      chat_history_load_error: "",
    });
  },

  computed: {
    active_conversation() {
      return this.conversations.find(
        (item) => item.id === this.active_conversation_id
      ) || null;
    },

    active_conversation_title() {
      return this.active_conversation
        ? this.get_conversation_title(this.active_conversation)
        : "Siyanda Platinum Assistant";
    },

    messages() {
      return this.active_conversation?.messages || [];
    },

    composer_placeholder() {
      if (this.scope?.level === "property" && this.scope.property_name) {
        return `Ask about ${this.scope.property_name} indicators, evidence, and progress...`;
      }
      if (this.scope?.level === "portfolio") {
        return "Ask about Siyanda operations, safety, sustainability, or performance...";
      }
      return "Ask me anything...";
    },
  },

  watch: {
    messages: {
      deep: true,
      handler() {
        this.scroll_to_latest_message();
      },
    },
    active_conversation_id(id) {
      this.save_active_conversation(id);
      this.ensure_active_conversation_messages(id);
      this.scroll_to_latest_message({ settle: true });
    },
    is_open(is_open_state) {
      document.body.classList.toggle("chatbot-lock", is_open_state);
      this.update_dock_layout();
      if (is_open_state) {
        this.retry_load_conversations_if_needed();
        this.scroll_to_latest_message({ settle: true });
      }
    },
    is_fullscreen() {
      this.update_dock_layout();
    },
  },

  mounted() {
    this.load_conversations();
    document.addEventListener("mousedown", this.handle_click_outside);
    document.addEventListener("touchstart", this.handle_click_outside);
    window.addEventListener("resize", this.update_dock_layout);
  },

  beforeUnmount() {
    this.clear_typewriter_timer();
    document.removeEventListener("mousedown", this.handle_click_outside);
    document.removeEventListener("touchstart", this.handle_click_outside);
    window.removeEventListener("resize", this.update_dock_layout);
    document.body.classList.remove("chatbot-lock", "chatbot-docked");
  },

  methods: {
    ...conversation_methods,
    ...chat_methods,
    ...query_methods,

    close_assistant() {
      this.$emit("close-assistant");
    },

    toggle_fullscreen() {
      this.is_fullscreen = !this.is_fullscreen;
    },

    toggle_history() {
      this.is_history_open = !this.is_history_open;
    },

    // Desktop: the panel docks under the page header and the page content
    // shrinks beside it (see body.chatbot-docked in style.css).
    update_dock_layout() {
      this.is_docked = window.innerWidth > 768;
      const header = document.querySelector(".dashboard-header");
      const header_bottom = header ? Math.max(header.getBoundingClientRect().bottom, 0) : 0;
      document.documentElement.style.setProperty("--chatbot-dock-top", `${header_bottom}px`);
      document.body.classList.toggle(
        "chatbot-docked",
        this.is_open && this.is_docked && !this.is_fullscreen
      );
    },

    handle_select_conversation(id) {
      this.select_conversation(id);
      if (this.is_docked && !this.is_fullscreen) this.is_history_open = false;
    },

    handle_click_outside(event) {
      if (!this.is_open || this.is_fullscreen || this.is_docked) return;
      const panel = this.$refs.chatbot;
      if (panel && !panel.contains(event.target)) this.close_assistant();
    },

    scroll_to_latest_message(options) {
      this.$nextTick(() => {
        this.$refs.chat_messages?.scroll_to_bottom(options);
      });
    },
  },
};
